/**
 * Preset configurations for common scenarios
 */

import type { SimConfig, PresetConfig } from '../sim/types';

export const PRESETS: Record<string, PresetConfig> = {
  // Euler top presets
  euler_tennis_racket: {
    name: 'Tennis Racket Effect',
    description: 'Intermediate axis instability (Dzhanibekov effect)',
    config: {
      type: 'euler',
      inertia: { I1: 1.0, I2: 2.0, I3: 3.0 },
      omega0: [0.05, 5.0, 0.05],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4'
    }
  },

  euler_stable_spin: {
    name: 'Stable Spin (Major Axis)',
    description: 'Rotation about maximum moment of inertia',
    config: {
      type: 'euler',
      inertia: { I1: 1.0, I2: 2.0, I3: 3.0 },
      omega0: [0.0, 0.0, 5.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4'
    }
  },

  euler_tumbling: {
    name: 'Chaotic Tumbling',
    description: 'Complex rotation with all axes active',
    config: {
      type: 'euler',
      inertia: { I1: 1.0, I2: 1.5, I3: 2.0 },
      omega0: [2.0, 3.0, 4.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4'
    }
  },

  // Lagrange top presets
  lagrange_sleeping: {
    name: 'Sleeping Top',
    description: 'Fast vertical spin, minimal precession',
    config: {
      type: 'lagrange',
      inertia: { I1: 1.0, I2: 1.0, I3: 0.5 },
      omega0: [0.0, 0.0, 20.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 1.0,
      com: [0, 0, 0.5],
      gravity: 9.81
    }
  },

  lagrange_precessing: {
    name: 'Precessing Top',
    description: 'Steady precession around vertical axis',
    config: {
      type: 'lagrange',
      inertia: { I1: 1.0, I2: 1.0, I3: 0.5 },
      omega0: [0.1, 0.1, 10.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 1.0,
      com: [0, 0, 0.5],
      gravity: 9.81
    }
  },

  lagrange_nutation: {
    name: 'Top with Nutation',
    description: 'Precession with wobbling (nutation)',
    config: {
      type: 'lagrange',
      inertia: { I1: 2.0, I2: 2.0, I3: 1.0 },
      omega0: [1.0, 1.0, 8.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 1.5,
      com: [0, 0, 0.3],
      gravity: 9.81
    }
  },

  lagrange_slow_spin: {
    name: 'Slow Wobbling Top',
    description: 'Falling top with complex motion',
    config: {
      type: 'lagrange',
      inertia: { I1: 1.0, I2: 1.0, I3: 0.5 },
      omega0: [0.5, 0.3, 3.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 1.0,
      com: [0, 0, 0.6],
      gravity: 9.81
    }
  },

  // Kovalevskaya top presets
  kovalevskaya_integrable: {
    name: 'Kovalevskaya Top (Classic)',
    description: 'Integrable motion with special symmetry',
    config: {
      type: 'kovalevskaya',
      inertia: { I1: 2.0, I2: 2.0, I3: 1.0 },
      omega0: [1.0, 2.0, 3.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 1.0,
      com: [0.5, 0, 0],
      gravity: 9.81
    }
  },

  kovalevskaya_fast: {
    name: 'Fast Kovalevskaya Top',
    description: 'Rapid rotation showing integrability',
    config: {
      type: 'kovalevskaya',
      inertia: { I1: 2.0, I2: 2.0, I3: 1.0 },
      omega0: [2.0, 3.0, 8.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 1.0,
      com: [0.3, 0, 0],
      gravity: 9.81
    }
  },

  kovalevskaya_heavy: {
    name: 'Heavy Kovalevskaya Top',
    description: 'Stronger gravitational influence',
    config: {
      type: 'kovalevskaya',
      inertia: { I1: 2.0, I2: 2.0, I3: 1.0 },
      omega0: [0.5, 1.0, 5.0],
      dt: 0.001,
      timeScale: 1.0,
      method: 'rk4',
      mass: 2.0,
      com: [0.7, 0, 0],
      gravity: 9.81
    }
  }
};

/**
 * Get presets for a specific top type
 */
export function getPresetsForType(type: 'euler' | 'lagrange' | 'kovalevskaya'): Record<string, PresetConfig> {
  const filtered: Record<string, PresetConfig> = {};

  for (const [key, preset] of Object.entries(PRESETS)) {
    if (preset.config.type === type) {
      filtered[key] = preset;
    }
  }

  return filtered;
}

/**
 * Validate configuration based on type
 */
export function validateConfig(config: SimConfig): string | null {
  // Check common fields
  if (config.dt <= 0 || config.dt > 0.1) {
    return 'Timestep must be between 0 and 0.1 seconds';
  }

  if (config.timeScale <= 0 || config.timeScale > 10) {
    return 'Time scale must be between 0 and 10';
  }

  // Check inertia
  if (config.inertia.I1 <= 0 || config.inertia.I2 <= 0 || config.inertia.I3 <= 0) {
    return 'All inertia values must be positive';
  }

  // Type-specific validation
  if (config.type === 'lagrange') {
    const { I1, I2 } = config.inertia;
    const tol = 1e-6;

    if (Math.abs(I1 - I2) > tol * Math.max(I1, I2)) {
      return 'Lagrange top requires I1 = I2 (symmetric top)';
    }

    if (!config.mass || config.mass <= 0) {
      return 'Lagrange top requires positive mass';
    }

    if (!config.gravity || config.gravity < 0) {
      return 'Lagrange top requires non-negative gravity';
    }
  }

  if (config.type === 'kovalevskaya') {
    const { I1, I2, I3 } = config.inertia;
    const tol = 1e-6;

    if (Math.abs(I1 - I2) > tol * I1) {
      return 'Kovalevskaya top requires I1 = I2';
    }

    if (Math.abs(I1 - 2 * I3) > tol * I1) {
      return 'Kovalevskaya top requires I1 = I2 = 2*I3';
    }

    if (!config.com || Math.abs(config.com[1]) > tol || Math.abs(config.com[2]) > tol) {
      return 'Kovalevskaya top requires center of mass on x-axis';
    }

    if (!config.mass || config.mass <= 0) {
      return 'Kovalevskaya top requires positive mass';
    }

    if (!config.gravity || config.gravity < 0) {
      return 'Kovalevskaya top requires non-negative gravity';
    }
  }

  return null;
}
